import { useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  value?: string;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  onChange: (value: string) => void; // empty string = cleared
}

/**
 * A searchable single-select dropdown.
 */
export const SearchableSelect = ({
  value,
  options,
  placeholder = "Select…",
  disabled,
  allowClear = true,
  className,
  onChange,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-8 w-full items-center justify-between gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs ring-offset-white focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950",
            className,
          )}
        >
          <span
            className={cn(
              "truncate",
              !value && "text-slate-500 dark:text-slate-400",
            )}
          >
            {value || placeholder}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="flex items-center gap-2 border-b px-2 py-1.5">
          <Search className="h-3.5 w-3.5 opacity-50" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="h-6 w-full bg-transparent text-xs outline-none placeholder:text-slate-400"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {allowClear && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800"
              onClick={() => {
                onChange("");
                setOpen(false);
                setQuery("");
              }}
            >
              <X className="h-3.5 w-3.5" />
              None (leave empty)
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground">
              No matches
            </div>
          ) : (
            filtered.map((opt, idx) => (
              <button
                type="button"
                key={`${opt}-${idx}`}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800",
                  opt === value && "font-medium",
                )}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <span className="truncate">{opt}</span>
                {opt === value && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
